import { StrictMode, useState } from "react"
import { createRoot } from "react-dom/client"

import { ToastContainer, TooltipProvider } from "@/components/ui"
import { IS_DEV } from "@/lib/constants"
import { LumenDevtools } from "@/src/components/Devtools"
import { VersionChecker } from "@/src/components/VersionChecker"
import { FeatureFlagsProvider } from "@/src/providers/FeatureFlags"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"

import "../sentry.client.config"
import "../styles/tailwind.css"
import { GOOGLE_MAPS_API_KEY } from "./constants/gmaps"
import Router from "./router/index"

const rootEl = document.getElementById("root")!

createRoot(rootEl).render(
  <StrictMode>
    <MyApp />
  </StrictMode>,
)

declare global {
  interface Window {
    lumen: {
      queryClient: QueryClient
      pdf?: {
        width: number
        height: number
      }
    }
  }
}

function MyApp() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Disable the refetch while developing so the page isn't constantly fetching.
            // https://tanstack.com/query/v4/docs/framework/react/guides/window-focus-refetching
            refetchOnWindowFocus: !IS_DEV,
            // Low default stale time so data is fresh, but high enough to not refetch multiple times if the same query is called multiple times in a short span of time.
            staleTime: 5000,
          },
        },
      }),
  )
  if (typeof window !== "undefined") {
    window.lumen = { ...window.lumen, queryClient }
  }

  return (
    <>
      <script
        async
        src={`https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&v=beta&libraries=maps3d,drawing,geometry,places,marker`}
      />
      <QueryClientProvider client={queryClient}>
        <FeatureFlagsProvider>
          <TooltipProvider>
            <VersionChecker />
            <Router />
          </TooltipProvider>
        </FeatureFlagsProvider>
        <ToastContainer />
        {IS_DEV ? <ReactQueryDevtools initialIsOpen={false} /> : null}
        {IS_DEV ? <LumenDevtools /> : null}
      </QueryClientProvider>
    </>
  )
}
